import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { WeeklyBar } from '../report.model';
import { ChartTheme, paletteFor } from './chart-palette';
import {
  AxisTick,
  GridLine,
  PlotMargin,
  bandCentre,
  displayCode,
  plotBox,
  round,
  yTickX,
} from './chart-geometry';
import {
  ChartTooltipService,
  chartWidthSignal,
  compactViewportSignal,
} from './chart-tooltip.service';

const MARGIN: PlotMargin = { top: 16, right: 8, bottom: 4, left: 8 };
/** Place réservée aux libellés « Semaine n » de l'axe des ordonnées. */
const AXIS_WIDTH = 92;
const X_AXIS_HEIGHT = 30;
/** Largeur de repli tant que le conteneur n'est pas mesuré. */
const FALLBACK_WIDTH = 636;
/** `barCategoryGap` 28 % et `maxBarSize` 40, repris de Recharts. */
const BAND_FILL = 0.72;
const MAX_BAR_WIDTH = 40;

interface WeeklyBarView {
  code: string;
  label: string;
  week: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
}

/**
 * Plan de remédiation, une colonne par compétence.
 *
 * L'ordonnée est **ordinale** : elle porte des semaines, pas une grandeur. Une
 * barre ne part donc pas de la ligne de base — elle flotte à l'intérieur de la
 * bande de sa semaine, sur 70 % de sa hauteur, pour se lire « programmée en
 * semaine n » et non « n unités ».
 *
 * Le nombre de semaines vient du back (`ReportView.planWeeks`) et non d'un 4
 * codé en dur : la longueur du plan dépend du nombre de lacunes à combler.
 */
@Component({
  selector: 's2e-weekly-chart',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'block'" },
  template: `
    <svg
      class="chart-svg weekly__svg"
      [attr.width]="width()"
      [attr.height]="height()"
      [attr.viewBox]="viewBox()"
      role="img"
      [attr.aria-label]="ariaLabel() || null"
      focusable="false"
      (mouseleave)="tooltip.hide()"
    >
      <g class="chart-grid">
        @for (line of gridLines(); track line.key) {
          <line
            [attr.x1]="line.x1"
            [attr.y1]="line.y1"
            [attr.x2]="line.x2"
            [attr.y2]="line.y2"
            [attr.stroke]="palette().grid"
            stroke-width="1"
          ></line>
        }
      </g>
      <line
        [attr.x1]="baseline().x1"
        [attr.y1]="baseline().y1"
        [attr.x2]="baseline().x2"
        [attr.y2]="baseline().y2"
        [attr.stroke]="palette().axis"
        stroke-width="1"
      ></line>
      <g class="chart-axis chart-axis--y">
        @for (tick of yTicks(); track tick.key) {
          <text
            [attr.x]="tick.x"
            [attr.y]="tick.y"
            [attr.text-anchor]="tick.anchor"
            dominant-baseline="central"
            [attr.fill]="palette().textMuted"
            font-size="12"
          >{{ tick.label }}</text>
        }
      </g>
      <g class="chart-axis chart-axis--x">
        @for (tick of xTicks(); track tick.key) {
          <text
            [attr.x]="tick.x"
            [attr.y]="tick.y"
            text-anchor="middle"
            dominant-baseline="central"
            [attr.fill]="palette().textSecondary"
            font-size="12"
            [attr.direction]="codeDir()"
          >{{ tick.label }}</text>
        }
      </g>
      <g class="weekly__bars">
        @for (bar of barViews(); track bar.code) {
          <rect
            class="weekly__bar"
            [attr.data-code]="bar.code"
            [attr.data-week]="bar.week"
            [attr.x]="bar.x"
            [attr.y]="bar.y"
            [attr.width]="bar.width"
            [attr.height]="bar.height"
            rx="14"
            ry="14"
            [attr.fill]="bar.fill"
            (mouseenter)="enter(bar, $event)"
            (mousemove)="tooltip.move($event)"
          ></rect>
        }
      </g>
    </svg>
  `,
})
export class WeeklyChartComponent {
  readonly tooltip = inject(ChartTooltipService);

  readonly bars = input.required<readonly WeeklyBar[]>();
  /** Longueur du plan ; borne l'échelle ordinale des ordonnées. */
  readonly weeks = input.required<number>();
  readonly theme = input.required<ChartTheme>();
  readonly rtl = input.required<boolean>();
  /** Libellés déjà traduits, index 0 = semaine 1. */
  readonly weekLabels = input.required<readonly string[]>();
  readonly codeDir = input.required<'rtl' | 'ltr'>();

  /** « Semaine programmée » — libellé de la ligne d'infobulle, déjà traduit. */
  readonly scheduledInLabel = input('');
  /** Libellé accessible complet, déjà traduit par la section. */
  readonly ariaLabel = input('');
  /** Préfixe de code affiché. `data-code` garde le code brut. */
  readonly codePrefix = input('C');

  private readonly compact = compactViewportSignal();
  private readonly measured = chartWidthSignal(FALLBACK_WIDTH);

  readonly width = computed(() => this.measured());
  readonly height = computed(() => (this.compact() ? 300 : 380));
  readonly viewBox = computed(() => `0 0 ${this.width()} ${this.height()}`);
  readonly palette = computed(() => paletteFor(this.theme()));

  private readonly box = computed(() =>
    plotBox(this.width(), this.height(), MARGIN, AXIS_WIDTH, X_AXIS_HEIGHT, this.rtl()),
  );

  /** Ordonnée d'une valeur de semaine, échelle ordinale sur `weeks`. */
  private yFor(value: number): number {
    const box = this.box();
    const weeks = Math.max(1, this.weeks());
    return box.bottom - (value / weeks) * box.height;
  }

  /**
   * Ordre des colonnes.
   *
   * Miroir en RTL comme les autres graphiques cartésiens : la première
   * compétence du plan doit tomber du côté par lequel on commence à lire.
   */
  private readonly ordered = computed(() => {
    const list = this.bars().slice();
    return this.rtl() ? list.reverse() : list;
  });

  private readonly band = computed(() => {
    const count = this.ordered().length;
    return count === 0 ? 0 : this.box().width / count;
  });

  readonly gridLines = computed<GridLine[]>(() => {
    const box = this.box();
    const lines: GridLine[] = [];
    for (let week = 1; week <= this.weeks(); week += 1) {
      const y = round(this.yFor(week));
      lines.push({ key: week, x1: round(box.left), y1: y, x2: round(box.right), y2: y });
    }
    return lines;
  });

  readonly baseline = computed(() => {
    const box = this.box();
    const y = round(box.bottom);
    return { x1: round(box.left), y1: y, x2: round(box.right), y2: y };
  });

  readonly yTicks = computed<AxisTick[]>(() => {
    const box = this.box();
    const rtl = this.rtl();
    const labels = this.weekLabels();
    const ticks: AxisTick[] = [];
    for (let week = 1; week <= this.weeks(); week += 1) {
      ticks.push({
        key: week,
        x: yTickX(box, rtl),
        y: round(this.yFor(week)),
        label: labels[week - 1] ?? '',
        anchor: rtl ? 'start' : 'end',
      });
    }
    return ticks;
  });

  readonly xTicks = computed<AxisTick[]>(() => {
    const box = this.box();
    const band = this.band();
    const prefix = this.codePrefix();
    return this.ordered().map((bar, index) => ({
      key: bar.code,
      x: round(bandCentre(box, band, index)),
      y: round(box.bottom + 16),
      label: displayCode(bar.code, prefix),
      anchor: 'middle' as const,
    }));
  });

  readonly barViews = computed<WeeklyBarView[]>(() => {
    const box = this.box();
    const band = this.band();
    if (band === 0) return [];
    const barWidth = Math.min(MAX_BAR_WIDTH, band * BAND_FILL);

    return this.ordered().map((bar, index) => {
      const centre = bandCentre(box, band, index);
      // 0,15 de marge en haut comme en bas : la barre occupe 70 % de sa bande.
      const top = this.yFor(bar.week - 0.15);
      const bottom = this.yFor(bar.week - 1 + 0.15);
      return {
        code: bar.code,
        label: bar.label,
        week: bar.week,
        x: round(centre - barWidth / 2),
        y: round(top),
        width: round(barWidth),
        height: round(bottom - top),
        fill: bar.color,
      };
    });
  });

  enter(bar: WeeklyBarView, event: MouseEvent): void {
    this.tooltip.show(
      `${displayCode(bar.code, this.codePrefix())} · ${bar.label}`,
      [
        {
          label: this.scheduledInLabel(),
          value: this.weekLabels()[bar.week - 1] ?? '',
          color: bar.fill,
        },
      ],
      event,
    );
  }
}
